<template>
  <footer class="bg-brand-fill text-white">
    <div class="mx-auto flex w-full max-w-7xl flex-col gap-10 px-4 py-12 md:px-8">
      <!-- Company + legal links -->
      <div class="flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
        <div class="flex flex-col gap-5 sm:flex-row sm:items-center sm:gap-6">
          <BrandLogo
            :name="branding.companyLogo"
            :label="branding.portalName"
            class="hidden h-12 w-auto text-white"
          />
          <address class="text-sm not-italic leading-relaxed text-white/80 flex gap-2">
            {{ companyAddress }}
          </address>
        </div>

        <nav :aria-label="t('footer.legalHeading')">
          <ul class="flex flex-wrap items-center gap-x-8 gap-y-2">
            <li v-for="link in availableLinks" :key="link.key">
              <a
                class="text-sm text-white transition-opacity hover:opacity-70"
                :href="t(link.hrefKey)"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ t(link.labelKey) }}
              </a>
            </li>
          </ul>
        </nav>
      </div>

      <!-- Legal disclaimer -->
      <div class="space-y-4 text-xs leading-relaxed text-white/60">
        <p v-for="paragraph in disclaimer" :key="paragraph">{{ paragraph }}</p>
      </div>

      <!-- Powered by -->
      <div v-if="branding.partners.length" class="flex flex-col items-center gap-4">
        <span class="text-[11px] font-bold uppercase tracking-[0.2em] text-white/60">
          {{ t("footer.poweredBy") }}
        </span>
        <ul class="flex flex-wrap items-center justify-center gap-x-10 gap-y-5">
          <li v-for="partner in branding.partners" :key="partner.name">
            <a
              class="block text-white transition-opacity hover:opacity-70"
              :href="partner.href"
              target="_blank"
              rel="noopener noreferrer"
            >
              <BrandLogo
                :name="partner.logo"
                :label="partner.name"
                class="h-6 w-auto md:h-7"
              />
            </a>
          </li>
        </ul>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "@/lib/i18n";
import BrandLogo from "@/components/brand/BrandLogo.vue";
import { getBranding } from "@/lib/branding";
import { useConfig } from "@/lib/config";

const { t } = useI18n();
const branding = getBranding(useConfig().branding);

const links = [
  { key: "terms", labelKey: "footer.links.terms.label", hrefKey: "footer.links.terms.href" },
  { key: "privacy", labelKey: "footer.links.privacy.label", hrefKey: "footer.links.privacy.href" },
  { key: "imprint", labelKey: "footer.links.imprint.label", hrefKey: "footer.links.imprint.href" },
] as const;
const availableLinks = computed(() => links.filter((link) => t(link.hrefKey)));
const companyAddress = computed(() =>
  ["name", "street", "city"]
    .map((key) => t(`footer.company.${key}`))
    .filter(Boolean)
    .join(" - "),
);

const disclaimer = computed(() =>
  (["p1", "p2", "p3", "p4", "p5", "p6", "p7"] as const)
    .map((key) => t(`footer.disclaimer.${key}`))
    .filter(Boolean),
);
</script>
